"use client";

import { useRouter } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import { HatGlasses } from "lucide-react";
import { Button, CustomTooltip } from "@/app/components/ui";
import { useStateHistoryChatPage } from "@/app/store/chat";
import { cn } from "@/app/lib/utils";
import { TEMPORARY_CHAT_ROUTE_ID } from "@/app/lib/constants";

interface TemporaryChatToggleProps {
   className?: string;
   size?: "default" | "sm" | "xs" | "icon";
}

export function TemporaryChatToggle({
   className,
   size = "icon"
}: TemporaryChatToggleProps) {
   const router = useRouter();
   const { isTemporaryChat, setTemporaryChat, setHistoryItem, resetState } =
      useStateHistoryChatPage();
   const translation = useTranslations("Home");

   const handleToggle = () => {
      const newValue = !isTemporaryChat;
      setTemporaryChat(newValue);
      if (newValue) {
         setHistoryItem(null);
         router.push(`/c/${TEMPORARY_CHAT_ROUTE_ID}`);
      } else {
         resetState();
         router.push("/");
      }
   };

   return (
      <CustomTooltip
         text={
            isTemporaryChat
               ? translation("temporaryChatOn")
               : translation("temporaryChatEnable")
         }
         position="bottom"
      >
         <Button
            variant={isTemporaryChat ? "default" : "outline"}
            size={size}
            onClick={handleToggle}
            aria-label={
               isTemporaryChat
                  ? translation("temporaryChatOn")
                  : translation("temporaryChatEnable")
            }
            className={cn(
               "rounded-full relative hover:bg-gray-200 dark:hover:bg-gray-700",
               isTemporaryChat && "bg-transparent border-[1px] border-primary",
               className
            )}
         >
            <HatGlasses
               className={cn(
                  "h-[1.2rem] w-[1.2rem]",
                  isTemporaryChat ? "text-primary dark:text-slate-100" : "text-foreground"
               )}
            />
         </Button>
      </CustomTooltip>
   );
}
